// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {ModalEl} from 'common/components/modal';
import {InputEl} from 'common/components/input';
import {InputType} from 'common/components/input/types';
import {BannerEl} from 'common/components/banner';
import {BannerTheme, BannerType} from 'common/components/banner/types';
import {SelectEl} from 'common/components/select';
import {InputLabelEl} from 'common/components/input/label';
import {formatText} from 'common/util/format-text';

import type {FeedbackModal} from './index';
import {FeedbackForFeature, Sentiment} from './types';

type SentimentButtonProps = {
  sentiment: Sentiment;
  cmp: FeedbackModal;
};
function SentimentButton(props: SentimentButtonProps) {
  const sentiment: Sentiment = props.sentiment;
  const cmp: FeedbackModal = props.cmp;
  return (
    <ButtonEl
      sel='.sentiment-button'
      attrs={{
        theme: ButtonTheme.Select,
        pressed: cmp.state.sentiment === sentiment,
        icon: sentiment === Sentiment.Positive ? `thumbs-up` : `thumbs-down`,
      }}
      on={{click: () => cmp.update({sentiment})}}
    >
      {sentiment === Sentiment.Positive ? cmp.positiveLabel : cmp.negativeLabel}
    </ButtonEl>
  );
}

export function Template(this: FeedbackModal) {
  const FeedbackPrompt = () => {
    const isSubmitDisabled = this.state.saving || (!this.state.feedback && !this.state.sentiment);

    return (
      <>
        <div sel='.content'>
          <div sel='.title'>{this.title}</div>
          {this.state.submitErrorMsg && (
            <BannerEl
              theme={BannerTheme.Error}
              type={BannerType.Embedded}
              bodyText={`Failed to submit feedback: ${this.state.submitErrorMsg}`}
            />
          )}
          {this.attr(`feature`) !== FeedbackForFeature.General && (
            <div sel='.sentiment'>
              <div sel='.sentiment-prompt'>{this.sentimentHeading}</div>
              <div sel='.sentiment-controls'>
                <SentimentButton sentiment={Sentiment.Positive} cmp={this} />
                <SentimentButton sentiment={Sentiment.Negative} cmp={this} />
              </div>
            </div>
          )}
          {this.canDelegateFeedback() ? (
            <>
              <div sel='.customer-email-input'>
                <InputEl
                  params={{
                    type: InputType.Text,
                    label: this.t(`help.feedback.email-prompt`),
                    placeholder: this.t(`help.feedback.email-placeholder`),
                    onInput: this.handleCustomerEmailInput,
                  }}
                  props={{value: this.state.customerEmail}}
                />
              </div>
              <div sel='.opportunity-id-input'>
                <InputEl
                  params={{
                    type: InputType.Text,
                    label: this.t(`help.feedback.sfdc-prompt`),
                    placeholder: this.t(`help.feedback.sfdc-placeholder`),
                    onInput: this.handleSfdcOpportunityId,
                  }}
                  props={{value: this.state.sfdcOpportunityId}}
                />
              </div>
              <div sel='.feedback-type-select'>
                <InputLabelEl
                  attrs={{
                    label: this.t(`help.feedback.type-prompt`),
                  }}
                />
                <SelectEl
                  attrs={{
                    'menu-width': 300,
                  }}
                  props={{
                    sections: this.getFeedbackTypeSections(),
                  }}
                  on={{
                    change: (ev: CustomEvent) => this.handleSelectFeedbackType(ev.detail.item),
                  }}
                />
              </div>
            </>
          ) : null}
          <div sel='.feedback-input'>
            <InputEl
              params={{
                type: InputType.TextArea,
                placeholder: this.placeholder,
                onInput: this.handleFeedbackInput,
              }}
              props={{value: this.state.feedback}}
            />
          </div>
          <div>{formatText(this.attr(`talk-to-pm-override`) || this.t(`help.feedback.schedule-pm-call`))}</div>
        </div>
        <div sel='.actions'>
          <ButtonEl
            sel='.cancel-btn'
            attrs={{
              theme: ButtonTheme.Tertiary,
              role: `button`,
            }}
            on={{click: () => this.closeModal(false)}}
          >
            {this.t(`help.feedback.cancel`)}
          </ButtonEl>
          <ButtonEl
            sel='.submit-btn'
            attrs={{
              theme: ButtonTheme.Primary,
              disabled: isSubmitDisabled,
              icon: this.state.saving ? `spinner` : ``,
              style: isSubmitDisabled ? `pointer-events: none;` : ``,
            }}
            on={{click: this.submitFeedback}}
          >
            {this.state.saving ? this.t(`help.feedback.submit-pending`) : this.submitButtonText}
          </ButtonEl>
        </div>
      </>
    );
  };

  return (
    <ModalEl
      sel='.feedback-modal'
      attrs={{open: true, closeable: true, 'grow-to-content': true, role: `dialog`, 'aria-label': this.title}}
      on={{change: this.handleModalChange}}
    >
      <div sel='.body'>
        <FeedbackPrompt />
      </div>
    </ModalEl>
  );
}
